import { useBack } from '../../../navigation/navigation'
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useEntityNavigate as useNavigate, EntityLink as Link } from '../../../navigation/navigation'
import api from '../../../services/api'
import { useAuth } from '../../../contexts/AuthContext'
import { useEmployeeNotificationsOptional } from '../../../contexts/EmployeeNotificationsContext'
import { usePageHeader } from '../../../contexts/PageHeaderContext'
import Icon from '../../../components/Icon'
import EntityHistory from '../../../components/EntityHistory'
import ConfirmModal from '../../../components/ConfirmModal'
import {
  Button,
  FormGroup,
  Input,
  SearchableSelect,
  SegmentedControl,
  Select,
  TableShell,
} from '../../../components/ui'
import { translateApiError } from '../../../utils/errorMessages'
import EmployeeAllocationBoard from './EmployeeAllocationBoard'
import EmployeeDetailHeader from './EmployeeDetailHeader'
import EmployeeMainTabs from './EmployeeMainTabs'
import {
  buildEmployeeIncomeModel,
  buildTeamOptions,
  normalizeTeamName,
} from './employeeDetailUtils'
import { getEmployeeDepartmentTeamDataUrl } from './employeeDepartmentTeamLink'
import {
  getBudgetCardPath,
  getEmployeeStageTypeKey,
  getSharedBudgetCardPath,
  groupItemsByObjectAndType,
  pluralizeStageBudgets,
} from '../stageTableGrouping'
import '../Company.css'
import '../department-detail/DepartmentDetail.css'
import '../EmployeeProjectsTable.css'
import './EmployeeDetail.css'

const SECTION_IDS = { income: 'income', projects: 'projects', allocation: 'allocation' }

const CURRENT_YEAR = new Date().getFullYear()
const YEAR_OPTIONS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR]
const PERIOD_OPTIONS = [
  { value: 'q1', label: 'I кв' },
  { value: 'q2', label: 'II кв' },
  { value: 'q3', label: 'III кв' },
  { value: 'q4', label: 'IV кв' },
  { value: 'year', label: 'Год' },
]
const QUARTER_LABELS = {
  1: 'I',
  2: 'II',
  3: 'III',
  4: 'IV',
}
const REMAINING_FILTERS = [
  { value: 'all', label: 'Все' },
  { value: 'active', label: 'В работе' },
  { value: 'new', label: 'Новые' },
]

function pluralizeRu(n, one, few, many) {
  const x = Math.abs(Number(n)) % 100
  const y = x % 10
  if (x > 10 && x < 20) return many
  if (y === 1) return one
  if (y > 1 && y < 5) return few
  return many
}

function getStageStatusTone(status) {
  const value = String(status || '').toLowerCase()
  if (value.includes('просроч')) return 'ds-status-pill-overdue'
  if (value.includes('сдан') || value.includes('заверш') || value.includes('закры')) return 'ds-status-pill-done'
  if (value.includes('нов')) return 'ds-status-pill-draft'
  return 'ds-status-pill-active'
}

function isDoneStageStatus(status) {
  const value = String(status || '').toLowerCase()
  return value.includes('сдан') || value.includes('заверш') || value.includes('закры') || value.includes('заакт')
}

function isNewStageStatus(status) {
  const value = String(status || '').trim().toLowerCase()
  return value === 'новая' || value === 'новый' || value === 'не в работе'
}

function getStageVisualStatus(status, { dueDate, isInvalidLegacyDate: checkInvalidDate } = {}) {
  const rawStatus = String(status || '').trim()
  const normalized = rawStatus.toLowerCase()
  const date = dueDate ? new Date(dueDate) : null
  const isInvalidDate = dueDate && typeof checkInvalidDate === 'function'
    ? checkInvalidDate(dueDate)
    : false
  const isOverdue = Boolean(
    dueDate &&
    date &&
    !Number.isNaN(date.getTime()) &&
    !isInvalidDate &&
    date < new Date() &&
    !isDoneStageStatus(rawStatus)
  )

  if (normalized.includes('просроч') || isOverdue) {
    return { label: 'Просрочена', tone: 'ds-status-pill-overdue', isOverdue: true }
  }
  if (isDoneStageStatus(rawStatus)) {
    return { label: 'Сдано', tone: 'ds-status-pill-done', isOverdue: false }
  }
  if (isNewStageStatus(rawStatus)) {
    return { label: 'Новая', tone: 'ds-status-pill-draft', isOverdue: false }
  }
  if (!rawStatus || rawStatus === '-' || normalized.includes('в работе')) {
    return { label: 'В работе', tone: 'ds-status-pill-active', isOverdue: false }
  }
  return { label: rawStatus || 'В работе', tone: getStageStatusTone(rawStatus || 'В работе'), isOverdue: false }
}

function getStageStatusPillClass(visualStatus) {
  return `employee-income-status-pill ${String(visualStatus?.tone || 'ds-status-pill-active').replace('ds-status-pill', 'employee-income-status-pill')}`
}

function hasVisibleRemainingShare(stage) {
  return Math.round(Number(stage?.remaining || 0)) > 0
}

function EmployeeCard() {
  const back = useBack('/company/team')
  const { name } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [employee, setEmployee] = useState(null)
  const [loading, setLoading] = useState(true)
  const [employeeToDelete, setEmployeeToDelete] = useState(null)
  const [employeeDeleteError, setEmployeeDeleteError] = useState(null)
  const [workPercentages, setWorkPercentages] = useState({})
  const [searchQuery, setSearchQuery] = useState('')
  const [teamFilter, setTeamFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [stageTypeFilter, setStageTypeFilter] = useState('')
  const [dueDateFilter, setDueDateFilter] = useState('')
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR)
  const [periodType, setPeriodType] = useState('quarter')
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1)
  const [selectedTeam, setSelectedTeam] = useState('all')
  const [remainingFilter, setRemainingFilter] = useState('all')
  const canManageEmployees = user?.role === 'admin' || user?.role === 'management'
  const [allocationStages, setAllocationStages] = useState([])
  const [allocationLoading, setAllocationLoading] = useState(false)
  const [allocationUpdatingStageId, setAllocationUpdatingStageId] = useState(null)
  const [allocationMenuStageId, setAllocationMenuStageId] = useState(null)
  const [draggedAllocationStageId, setDraggedAllocationStageId] = useState(null)
  const [allocationModalOpen, setAllocationModalOpen] = useState(false)
  const employeeNotifications = useEmployeeNotificationsOptional()
  const payoutRequests = employeeNotifications?.payoutRequests ?? []
  const openPayoutModal = employeeNotifications?.openPayoutModal ?? (() => {})
  const [activeMainTab, setActiveMainTab] = useState(SECTION_IDS.income)
  const [collapsedStageObjects, setCollapsedStageObjects] = useState(() => new Set())
  const [expandedTypeGroups, setExpandedTypeGroups] = useState(() => new Set())
  const [allocationCatalogLoading, setAllocationCatalogLoading] = useState(false)
  const [allocationCatalog, setAllocationCatalog] = useState([])
  const [allocationObjectSearch, setAllocationObjectSearch] = useState('')
  const [allocationContractSearch, setAllocationContractSearch] = useState('')
  const [allocationStageSearch, setAllocationStageSearch] = useState('')
  const [allocationObjectId, setAllocationObjectId] = useState('')
  const [allocationContractId, setAllocationContractId] = useState('')
  const [allocationStageId, setAllocationStageId] = useState('')
  const preventCardClickRef = useRef(false)
  const isEmployeeRole = user?.role === 'employee'
  const isOwnEmployeeCardById = !!employee?.id && !!user?.employee_id && String(employee.id) === String(user.employee_id)
  const decodedEmployeeName = decodeURIComponent(name || '').trim().toLowerCase()
  const userFullName = String(user?.full_name || '').trim().toLowerCase()
  const isOwnEmployeeCardByName = !!decodedEmployeeName && !!userFullName && decodedEmployeeName === userFullName
  const isOwnEmployeeCard = isOwnEmployeeCardById || isOwnEmployeeCardByName
  const canEditAllocation = !isEmployeeRole || isOwnEmployeeCard

  usePageHeader({
    title: null,
    hideTopHeader: true,
    renderActions: () => null,
  }, [])

  useEffect(() => {
    fetchEmployee()
  }, [name, selectedYear, periodType, selectedQuarter, selectedTeam])

  useEffect(() => {
    if (employee?.id) {
      fetchEmployeeAllocations(employee.id)
    } else {
      setAllocationStages([])
    }
  }, [employee?.id])

  useEffect(() => {
    const onDocumentClick = () => setAllocationMenuStageId(null)
    document.addEventListener('click', onDocumentClick)
    return () => document.removeEventListener('click', onDocumentClick)
  }, [])

  const fetchEmployee = async () => {
    try {
      setLoading(true)
      const decodedName = decodeURIComponent(name || '')
      const employeePath = /^[a-zA-Z0-9_-]+$/.test(decodedName)
        ? `/company/employees/${encodeURIComponent(decodedName)}`
        : `/company/employees/by-name/${encodeURIComponent(decodedName)}`
      const response = await api.get(employeePath, {
        params: {
          year: selectedYear,
          period: periodType,
          quarter: selectedQuarter,
          team: selectedTeam,
        }
      })
      if (response.data && response.data.employee) {
        const emp = response.data.employee
        setEmployee(emp)
        if (String(emp.id) !== decodedName) navigate(`/company/team/${encodeURIComponent(emp.id)}`, { replace: true, state: location.state })
        if (emp.b2_stages) {
          const percentages = {}
          emp.b2_stages.forEach(stage => {
            percentages[stage.id_стадии] = 100
          })
          setWorkPercentages(percentages)
        }
      } else {
        alert('Сотрудник не найден')
        navigate('/company/team')
      }
    } catch (error) {
      console.error('Error fetching employee:', error)
      alert('Ошибка загрузки данных сотрудника: ' + translateApiError(error.response?.data?.error || error.message))
      navigate('/company/team')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return null
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    } catch {
      return dateString
    }
  }

  const formatCurrency = (amount) => {
    if (!amount) return '0 ₽'
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(amount)
  }

  const formatNumber = (value, digits = 2) => {
    if (value == null || value === '' || Number.isNaN(Number(value))) return '-'
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(Number(value))
  }

  const formatStageLabel = (stage) => {
    const contractNumber = (stage?.номер_договора || stage?.contract_number || '').trim()
    const stageType = (stage?.тип_распределения || stage?.distribution_type || stage?.тип_стадии || stage?.stage_type || '').trim()
    if (contractNumber && stageType) return `${contractNumber} - ${stageType}`
    return stageType || contractNumber || (stage?.id_стадии || stage?.stage_id || '-')
  }

  const handleWorkPercentageChange = (stageId, value) => {
    const numValue = Math.max(0, Math.min(100, parseFloat(value) || 0))
    setWorkPercentages(prev => ({ ...prev, [stageId]: numValue }))
  }

  const getEarnedAmount = (stage) => {
    const budget = parseFloat(stage.доход_сотрудника_по_стоимости || 0)
    const percentage = workPercentages[stage.id_стадии] || 100
    return budget * (percentage / 100)
  }

  const getActuatedAmount2025 = (stage) => parseFloat(stage.заактировано_в_2025 || 0)
  const getActuatedPreviousYears = (stage) => parseFloat(stage.доход_по_ранее_заактированной || 0)
  const getActuatedCombinedForRow = (stage) =>
    getActuatedAmount2025(stage) + (selectedYear === 2024 ? 0 : getActuatedPreviousYears(stage))
  const getStageStatus = (stage) => ((stage?.статус_стадии || '').trim() || '-')

  const shortStageTypeLabel = (raw) => {
    const t = String(raw || '').trim()
    if (!t) return '—'
    const map = {
      Корректировки: 'Корр.',
      Внедрение: 'Внедр.',
      'Проектная документация': 'ПД',
      'Рабочая документация': 'РД',
      'Экспертиза проекта': 'ЭП',
    }
    if (map[t]) return map[t]
    if (t.length <= 14) return t
    return `${t.slice(0, 12)}…`
  }

  const isInvalidLegacyDate = (dateString) => {
    if (!dateString) return false
    try {
      const y = new Date(dateString).getFullYear()
      return !Number.isFinite(y) || y < 1900 || y > 2100
    } catch {
      return true
    }
  }

  useEffect(() => {
    setStatusFilter('')
  }, [employee?.id])

  const teamOptions = useMemo(() => buildTeamOptions(employee || {}), [employee])
  const actualTeamOptions = useMemo(() => teamOptions.filter((option) => option.value !== 'all'), [teamOptions])
  const showTeamSegment = actualTeamOptions.length > 1
  const teamDataUrl = useMemo(() => getEmployeeDepartmentTeamDataUrl(employee || {}), [employee])

  useEffect(() => {
    if (!teamOptions.some((option) => option.value === selectedTeam)) {
      setSelectedTeam('all')
    }
  }, [teamOptions, selectedTeam])

  // Уникальные команды для фильтров проектов
  const uniqueTeams = useMemo(() => {
    return actualTeamOptions.map((option) => option.value)
  }, [actualTeamOptions])

  // Уникальные статусы из b2_stages
  const uniqueStatuses = useMemo(() => {
    if (!employee?.b2_stages?.length) return []
    const set = new Set()
    employee.b2_stages.forEach(s => {
      const status = (s.статус_стадии || '').trim()
      if (status) set.add(status)
    })
    return Array.from(set).sort()
  }, [employee?.b2_stages])

  // Уникальные типы стадий из b2_stages
  const uniqueStageTypes = useMemo(() => {
    if (!employee?.b2_stages?.length) return []
    const set = new Set()
    employee.b2_stages.forEach(s => {
      const t = (s.тип_стадии || '').trim()
      if (t) set.add(t)
    })
    return Array.from(set).sort()
  }, [employee?.b2_stages])

  // Уникальные периоды (годы) из сроков исполнения
  const uniqueDueDatePeriods = useMemo(() => {
    if (!employee?.b2_stages?.length) return []
    const set = new Set()
    employee.b2_stages.forEach(s => {
      if (s.срок_исполнения) {
        try {
          const date = new Date(s.срок_исполнения)
          const year = date.getFullYear()
          if (!isNaN(year)) set.add(year.toString())
        } catch (e) {
          // Игнорируем невалидные даты
        }
      }
    })
    return Array.from(set).sort((a, b) => parseInt(b) - parseInt(a))
  }, [employee?.b2_stages])

  // Стадии с бэка: stage_summary + стадии без плана (cost_after_tax=0) только из актов (см. company.py).
  const mergedProjects = useMemo(() => {
    if (!employee) return []
    return employee.b2_stages || []
  }, [employee])

  // Отфильтрованные стадии для таблицы проектов
  const filteredStages = useMemo(() => {
    if (!mergedProjects.length) return []
    return mergedProjects.filter(stage => {
      const team = normalizeTeamName(stage.команда)
      if (selectedTeam !== 'all' && team !== selectedTeam) return false
      if (teamFilter && team !== teamFilter) return false
      
      const status = (stage.статус_стадии || '').trim()
      if (statusFilter && status !== statusFilter) return false
      
      const stageType = (stage.тип_стадии || '').trim()
      if (stageTypeFilter && stageType !== stageTypeFilter) return false
      
      if (dueDateFilter) {
        if (!stage.срок_исполнения) return false
        try {
          const date = new Date(stage.срок_исполнения)
          const year = date.getFullYear().toString()
          if (year !== dueDateFilter) return false
        } catch (e) {
          return false
        }
      }
      
      if (!searchQuery) return true
      const q = searchQuery.toLowerCase()
      const obj = (stage.объект || '').toLowerCase()
      const sid = (stage.id_стадии || '').toLowerCase()
      const contract = (stage.номер_договора || '').toLowerCase()
      return obj.includes(q) || sid.includes(q) || contract.includes(q)
    })
  }, [mergedProjects, selectedTeam, teamFilter, statusFilter, stageTypeFilter, dueDateFilter, searchQuery])

  // Группировка стадий по Объекту, внутри по Команде, сортировка по названию стадии
  const groupedStages = useMemo(() => {
    if (!filteredStages.length) return {}
    
    const grouped = {}
    filteredStages.forEach(stage => {
      const object = (stage.объект || '').trim() || 'Без объекта'
      const team = normalizeTeamName(stage.команда)
      const stageName = (stage.id_стадии || '').trim() || ''
      
      if (!grouped[object]) {
        grouped[object] = {}
      }
      if (!grouped[object][team]) {
        grouped[object][team] = []
      }
      grouped[object][team].push(stage)
    })
    
    // Сортируем стадии внутри каждой команды по названию стадии
    Object.keys(grouped).forEach(object => {
      Object.keys(grouped[object]).forEach(team => {
        grouped[object][team].sort((a, b) => {
          const nameA = (a.id_стадии || '').trim()
          const nameB = (b.id_стадии || '').trim()
          return nameA.localeCompare(nameB, 'ru', { numeric: true, sensitivity: 'base' })
        })
      })
    })
    
    return grouped
  }, [filteredStages])

  const employeeObjectGroups = useMemo(() => (
    Object.keys(groupedStages).sort().map((object) => {
      const teams = groupedStages[object]
      const flatStages = []
      Object.keys(teams).sort().forEach((team) => {
        ;(teams[team] || []).forEach((stage) => flatStages.push({ ...stage, _team: team }))
      })
      const objectKey = `object:${object}`
      return {
        object,
        objectKey,
        itemCount: flatStages.length,
        firstStage: flatStages[0],
        typeGroups: groupItemsByObjectAndType(
          flatStages,
          () => objectKey,
          getEmployeeStageTypeKey,
        )[0]?.types || [],
      }
    })
  ), [groupedStages])

  const showTeamColumn = useMemo(() => {
    const teams = new Set(mergedProjects.map((s) => normalizeTeamName(s.команда || s.team)))
    return teams.size > 1
  }, [mergedProjects])

  const toggleStageObjectGroup = (objectName) => {
    setCollapsedStageObjects((current) => {
      const next = new Set(current)
      if (next.has(objectName)) next.delete(objectName)
      else next.add(objectName)
      return next
    })
  }

  const toggleTypeGroup = (typeGroupKey) => {
    setExpandedTypeGroups((current) => {
      const next = new Set(current)
      if (next.has(typeGroupKey)) next.delete(typeGroupKey)
      else next.add(typeGroupKey)
      return next
    })
  }

  const projectsTableTotals = useMemo(() => {
    const totals = { budget: 0, earned: 0, actYear: 0, actPrev: 0 }
    filteredStages.forEach((stage) => {
      if (stage.доход_сотрудника_по_стоимости != null) {
        const b = parseFloat(stage.доход_сотрудника_по_стоимости || 0)
        totals.budget += b
        const pct = (workPercentages[stage.id_стадии] ?? 100) / 100
        totals.earned += b * pct
      }
      totals.actYear += parseFloat(stage.заактировано_в_2025 || 0)
      totals.actPrev += parseFloat(stage.доход_по_ранее_заактированной || 0)
    })
    return totals
  }, [filteredStages, workPercentages])

  const projectsActuatedCombinedTotal = useMemo(
    () => projectsTableTotals.actYear + (selectedYear === 2024 ? 0 : projectsTableTotals.actPrev),
    [projectsTableTotals.actYear, projectsTableTotals.actPrev, selectedYear]
  )

  const projectsRemainTotal = useMemo(() => {
    return filteredStages.reduce((sum, stage) => {
      let earned = 0
      if (stage.доход_сотрудника_по_стоимости != null) {
        const b = parseFloat(stage.доход_сотрудника_по_стоимости || 0)
        const pct = (workPercentages[stage.id_стадии] ?? 100) / 100
        earned = b * pct
      }
      const act = getActuatedAmount2025(stage) + (selectedYear === 2024 ? 0 : getActuatedPreviousYears(stage))
      return sum + (earned - act)
    }, 0)
  }, [filteredStages, workPercentages, selectedYear])

  const stageTeamById = useMemo(() => {
    const map = new Map()
    ;(employee?.b2_stages || []).forEach((stage) => {
      const id = stage.id_стадии || stage.stage_id || stage.id
      if (id) map.set(String(id), normalizeTeamName(stage.команда || stage.team))
    })
    return map
  }, [employee?.b2_stages])

  const filteredAllocationStages = useMemo(() => {
    if (selectedTeam === 'all') return allocationStages
    return allocationStages.filter((item) => {
      const team = normalizeTeamName(item.team || item.команда || stageTeamById.get(String(item.stage_id || '')))
      return team === selectedTeam
    })
  }, [allocationStages, selectedTeam, stageTeamById])

  const stageSummaryById = useMemo(() => {
    const map = new Map()
    ;(employee?.b2_stages || []).forEach((stage) => {
      const id = stage.id_стадии || stage.stage_id || stage.id
      if (!id) return
      map.set(String(id), stage)
    })
    return map
  }, [employee?.b2_stages])

  const enrichedAllocationStages = useMemo(() => (
    filteredAllocationStages.map((item) => {
      const stage = stageSummaryById.get(String(item.stage_id || ''))
      return {
        ...item,
        team: normalizeTeamName(item.team || item.команда || stage?.команда || stage?.team),
        income_amount: stage?.доход_сотрудника_по_стоимости ?? stage?.employee_income_cost ?? stage?.income ?? null,
        stage_display_type: item.stage_type || stage?.тип_стадии || stage?.stage_type || stage?.type,
        stage_object: item.object_name || stage?.объект || stage?.object_name,
        client_name: item.client_name || stage?.клиент || stage?.client_name || '',
      }
    })
  ), [filteredAllocationStages, stageSummaryById])

  const incomeModel = useMemo(() => buildEmployeeIncomeModel(employee || {}, {
    periodType,
    year: selectedYear,
    quarter: selectedQuarter,
    team: selectedTeam,
  }), [employee, periodType, selectedYear, selectedQuarter, selectedTeam])

  const displayedPayoutRequest = useMemo(() => payoutRequests.find((req) => {
    if (Number(req.year) !== Number(selectedYear)) return false
    if (periodType === 'year') return req.period_type === 'year'
    return req.period_type === 'quarter' && Number(req.quarter) === Number(selectedQuarter)
  }) || null, [payoutRequests, selectedYear, periodType, selectedQuarter])

  const allocationByStageKey = useMemo(() => {
    const map = new Map()
    allocationStages.forEach((item) => {
      ;[item.stage_id, item.object_stage_id, item.legacy_stage_id].forEach((key) => {
        if (key) map.set(String(key), item)
      })
    })
    return map
  }, [allocationStages])

  const findAllocationByStageKey = (stageKey) => (
    stageKey ? allocationByStageKey.get(String(stageKey)) : undefined
  )

  const remainingRows = useMemo(() => {
    const stagesWithShare = incomeModel.remainingStages.filter(hasVisibleRemainingShare)
    if (remainingFilter === 'all') return stagesWithShare
    return stagesWithShare.filter((stage) => stage.statusBucket === remainingFilter)
  }, [incomeModel.remainingStages, remainingFilter])

  const remainingCounts = useMemo(() => {
    return incomeModel.remainingStages
      .filter(hasVisibleRemainingShare)
      .reduce((acc, row) => {
        acc.all += 1
        if (row.statusBucket === 'active') acc.active += 1
        if (row.statusBucket === 'new') acc.new += 1
        return acc
      }, { all: 0, active: 0, new: 0 })
  }, [incomeModel.remainingStages])

  const remainingRowsTotal = useMemo(
    () => remainingRows.reduce((sum, row) => sum + Number(row.remaining || 0), 0),
    [remainingRows]
  )

  const breakdownTotal = useMemo(
    () => incomeModel.breakdown.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [incomeModel.breakdown]
  )

  // Справочник стадий по их id: даёт доступ к названию объекта, номеру договора
  // и названию этапа (типа стадии) для отображения в таблицах доходов.
  const stageInfoById = useMemo(() => {
    const map = new Map()
    incomeModel.remainingStages.forEach((row) => {
      const key = String(row.stageId || '').trim()
      if (key && !map.has(key)) map.set(key, row.stage || {})
    })
    return map
  }, [incomeModel.remainingStages])

  // Подзаголовок строки: «Номер договора + Название этапа (тип стадии)».
  const buildStageSubtitle = ({ contractNumber, stageRaw } = {}) => {
    const stage = stageRaw || {}
    const contract = String(contractNumber || stage.номер_договора || stage.contract_number || '').trim()
    const stageType = String(stage.тип_стадии || stage.stage_type || stage.тип_распределения || '').trim()
    return [contract, stageType].filter(Boolean).join(' · ')
  }

  const mainSectionTabs = useMemo(() => {
    return [
      { id: SECTION_IDS.income, label: 'Доходы' },
      { id: SECTION_IDS.projects, label: 'Стадии', count: filteredStages.length },
      { id: SECTION_IDS.allocation, label: 'Доска', count: enrichedAllocationStages.length },
    ]
  }, [filteredStages.length, enrichedAllocationStages.length])

  const periodLabel = periodType === 'year'
    ? `${selectedYear} год`
    : `${QUARTER_LABELS[selectedQuarter] || selectedQuarter} квартал ${selectedYear}`

  const heroNote = useMemo(() => {
    if (incomeModel.heroAmount <= 0) return null
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentQuarter = Math.floor(now.getMonth() / 3) + 1
    if (periodType === 'year' && selectedYear === currentYear) return 'Эту сумму ты можешь получить, когда закроем год.'
    if (periodType === 'quarter' && selectedYear === currentYear && selectedQuarter === currentQuarter) {
      return 'Эту сумму ты можешь получить, когда закроем квартал.'
    }
    return null
  }, [incomeModel.heroAmount, periodType, selectedQuarter, selectedYear])

  const projectsExportRows = useMemo(() => {
    const rows = []
    Object.keys(groupedStages).sort().forEach((objectName) => {
      const teams = groupedStages[objectName] || {}
      Object.keys(teams).sort().forEach((teamName) => {
        const stages = teams[teamName] || []
        stages.forEach((stage) => {
          const budget = stage.доход_сотрудника_по_стоимости == null ? null : Number(stage.доход_сотрудника_по_стоимости || 0)
          const teamPercent = stage.личный_процент_сотрудника == null ? null : Number(stage.личный_процент_сотрудника || 0)
          const donePercent = stage.доход_сотрудника_по_стоимости == null ? null : Number(workPercentages[stage.id_стадии] ?? 100)
          const earnedAmount = stage.доход_сотрудника_по_стоимости == null ? null : Number(getEarnedAmount(stage) || 0)
          const actuatedCombined = Number(getActuatedCombinedForRow(stage) || 0)
          const remaining = earnedAmount == null ? null : earnedAmount - getActuatedAmount2025(stage) - (selectedYear === 2024 ? 0 : getActuatedPreviousYears(stage))
          rows.push({
            object: objectName,
            team: teamName,
            stageId: formatStageLabel(stage),
            budget,
            teamPercent,
            donePercent,
            earnedAmount,
            actuatedCombined,
            remaining,
            dueDate: formatDate(stage.срок_исполнения) || '-',
            status: getStageStatus(stage),
          })
        })
      })
    })
    return rows
  }, [groupedStages, selectedYear, workPercentages])

  const csvCell = (value, { numeric = false } = {}) => {
    if (value == null || value === '') return ''
    if (numeric) {
      const n = Number(value)
      return Number.isFinite(n) ? n.toFixed(2).replace('.', ',') : ''
    }
    const raw = String(value)
    const s = /^[=+\-@]/.test(raw) ? `'${raw}` : raw
    if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
  }

  const exportProjectsCsv = () => {
    if (!projectsExportRows.length) return
    const headers = [
      'Объект',
      '№ стадии',
      'Моя доля (бюджет)',
      '% работы',
      'Заактировано',
      'Ещё получишь',
      'Срок',
      'Статус',
    ]
    const lines = [headers.join(';')]
    projectsExportRows.forEach((row) => {
      lines.push([
        csvCell(row.object),
        csvCell(row.stageId),
        csvCell(row.budget, { numeric: true }),
        csvCell(row.donePercent, { numeric: true }),
        csvCell(row.actuatedCombined, { numeric: true }),
        csvCell(row.remaining, { numeric: true }),
        csvCell(row.dueDate),
        csvCell(row.status),
      ].join(';'))
    })
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `employee-projects-${(employee?.full_name || 'employee').replace(/[^a-zA-Z0-9_-]/g, '_')}-${selectedYear}.csv`
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 100)
  }

  const exportProjectsPdf = () => {
    if (!projectsExportRows.length) return
    const win = window.open('', '_blank')
    if (!win) {
      alert('Разрешите всплывающие окна для экспорта PDF')
      return
    }
    const escapeHtml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
    const tableRows = projectsExportRows.map((row) => `
      <tr>
        <td>${escapeHtml(row.object)}</td>
        <td>${escapeHtml(row.stageId)}</td>
        <td style="text-align:right">${row.budget == null ? '-' : formatNumber(row.budget, 0)}</td>
        <td style="text-align:right">${row.donePercent == null ? '-' : formatNumber(row.donePercent, 1)}%</td>
        <td style="text-align:right">${formatNumber(row.actuatedCombined, 0)}</td>
        <td style="text-align:right">${row.remaining == null ? '-' : formatNumber(row.remaining, 0)}</td>
        <td>${escapeHtml(row.dueDate)}</td>
        <td>${escapeHtml(row.status)}</td>
      </tr>
    `).join('')
    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Проекты — ${employee?.full_name || ''}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #111; }
            h1 { font-size: 18px; margin: 0 0 12px; }
            .meta { margin-bottom: 12px; color: #555; font-size: 12px; }
            table { border-collapse: collapse; width: 100%; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; vertical-align: top; }
            th { background: #f6f6f6; text-align: left; }
          </style>
        </head>
        <body>
          <h1>Проекты — ${escapeHtml(employee?.full_name || '-')}</h1>
          <div class="meta">Год: ${selectedYear} · Строк: ${projectsExportRows.length}</div>
          <table>
            <thead>
              <tr>
                <th>Объект</th>
                <th>№ стадии</th>
                <th>Моя доля (бюджет)</th>
                <th>% работы</th>
                <th>Заактировано</th>
                <th>Ещё получишь</th>
                <th>Срок</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `
    win.document.open()
    win.document.write(html)
    win.document.close()
    win.onload = () => {
      try {
        win.focus()
        win.print()
      } catch (e) {
        console.error('Print window error:', e)
      }
    }
  }

  const employeeActionItems = [
    {
      id: 'export-csv',
      label: 'Скачать CSV',
      icon: <Icon name="download" size={14} />,
      disabled: projectsExportRows.length === 0,
      onSelect: exportProjectsCsv,
    },
    {
      id: 'export-pdf',
      label: 'Скачать PDF',
      icon: <Icon name="download" size={14} />,
      disabled: projectsExportRows.length === 0,
      onSelect: exportProjectsPdf,
    },
    canManageEmployees && { id: 'divider-employee-management', type: 'divider' },
    canManageEmployees && {
      id: 'edit-employee',
      label: 'Редактировать сотрудника',
      icon: <Icon name="edit" size={14} />,
      onSelect: () => navigate(`/company/team/${employee?.id}/edit`),
    },
    canManageEmployees && {
      id: 'archive-employee',
      label: 'Архивировать сотрудника',
      icon: <Icon name="trash" size={14} />,
      danger: true,
      onSelect: () => setEmployeeToDelete(employee),
    },
  ].filter(Boolean)

  const allocationAvailableStages = useMemo(() => {
    const allocatedIds = new Set((allocationStages || []).map((s) => String(s.stage_id || '')))
    return (allocationCatalog || []).filter((item) => !allocatedIds.has(String(item.stage_id || '')))
  }, [allocationCatalog, allocationStages])

  const allocationObjectOptions = useMemo(() => {
    const q = allocationObjectSearch.trim().toLowerCase()
    const map = new Map()
    allocationAvailableStages.forEach((item) => {
      const id = String(item.object_id || '')
      if (!id) return
      const name = String(item.object_name || '')
      if (q && !name.toLowerCase().includes(q)) return
      if (!map.has(id)) map.set(id, name || 'Без объекта')
    })
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }, [allocationAvailableStages, allocationObjectSearch])

  const allocationContractOptions = useMemo(() => {
    const q = allocationContractSearch.trim().toLowerCase()
    const map = new Map()
    allocationAvailableStages.forEach((item) => {
      if (allocationObjectId && String(item.object_id || '') !== allocationObjectId) return
      const id = String(item.contract_id || '')
      if (!id) return
      const number = String(item.contract_number || '')
      if (q && !number.toLowerCase().includes(q)) return
      if (!map.has(id)) map.set(id, number || 'Без номера')
    })
    return Array.from(map, ([id, number]) => ({ id, number })).sort((a, b) => a.number.localeCompare(b.number, 'ru'))
  }, [allocationAvailableStages, allocationObjectId, allocationContractSearch])

  const allocationStageOptions = useMemo(() => {
    const q = allocationStageSearch.trim().toLowerCase()
    return allocationAvailableStages.filter((item) => {
      if (allocationObjectId && String(item.object_id || '') !== allocationObjectId) return false
      if (allocationContractId && String(item.contract_id || '') !== allocationContractId) return false
      if (!q) return true
      const sid = String(item.stage_id || '').toLowerCase()
      const type = String(item.stage_type || '').toLowerCase()
      const contract = String(item.contract_number || '').toLowerCase()
      const object = String(item.object_name || '').toLowerCase()
      return sid.includes(q) || type.includes(q) || contract.includes(q) || object.includes(q)
    })
  }, [allocationAvailableStages, allocationObjectId, allocationContractId, allocationStageSearch])

  const getSystemRoleLabel = (value) => {
    switch (value) {
      case 'admin':
        return 'Администратор'
      case 'management':
        return 'Руководство'
      case 'department_head':
        return 'Руководитель подразделения'
      case 'employee':
        return 'Сотрудник'
      case 'accountant':
        return 'Бухгалтер'
      default:
        return '-'
    }
  }

  const handleArchiveEmployee = async () => {
    if (!employee?.id) return
    try {
      setEmployeeDeleteError(null)
      await api.delete(`/company/employees/${employee.id}`)
      navigate('/company/team')
    } catch (error) {
      setEmployeeDeleteError(translateApiError(error.response?.data?.error || error.message))
    }
  }

  const fetchEmployeeAllocations = async (employeeId) => {
    try {
      setAllocationLoading(true)
      const response = await api.get(`/company/employees/${employeeId}/allocations`)
      setAllocationStages(response.data?.allocations || [])
    } catch (error) {
      console.error('Error fetching employee allocations:', error)
      setAllocationStages([])
    } finally {
      setAllocationLoading(false)
    }
  }

  const formatAllocationDate = (plannedDate, factDate) => {
    const dateValue = plannedDate || factDate
    if (!dateValue) return '-'
    return formatDate(dateValue)
  }

  const updateAllocationStatus = async (stageId, nextStatus) => {
    if (!canEditAllocation) return
    if (!employee?.id || !stageId) return
    try {
      setAllocationUpdatingStageId(stageId)
      await api.put(`/company/employees/${employee.id}/allocations/${encodeURIComponent(stageId)}/status`, {
        work_status: nextStatus,
      })
      setAllocationStages((prev) => prev.map((item) => (
        item.stage_id === stageId ? { ...item, work_status: nextStatus } : item
      )))
    } catch (error) {
      alert('Не удалось обновить статус: ' + translateApiError(error.response?.data?.error || error.message))
    } finally {
      setAllocationUpdatingStageId(null)
    }
  }

  const allocationColumns = [
    { id: 'new', title: 'Новая' },
    { id: 'in_progress', title: 'У меня в работе' },
    { id: 'done', title: 'Завершена' },
  ]

  const allocationStatusActions = [
    { status: 'in_progress', label: 'Взять в работу' },
    { status: 'done', label: 'Завершить' },
    { status: 'new', label: 'Новая' },
  ]

  const handleAllocationDrop = (targetStatus) => {
    if (!canEditAllocation) return
    if (!draggedAllocationStageId) return
    preventCardClickRef.current = true
    const stage = allocationStages.find((item) => item.stage_id === draggedAllocationStageId)
    setDraggedAllocationStageId(null)
    if (!stage) return
    const currentStatus = stage.work_status || 'new'
    if (currentStatus === targetStatus) return
    updateAllocationStatus(draggedAllocationStageId, targetStatus)
  }

  const openAllocationModal = async () => {
    if (!canEditAllocation) return
    if (!employee?.id) return
    setAllocationModalOpen(true)
    setAllocationCatalogLoading(true)
    setAllocationObjectSearch('')
    setAllocationContractSearch('')
    setAllocationStageSearch('')
    setAllocationObjectId('')
    setAllocationContractId('')
    setAllocationStageId('')
    try {
      const response = await api.get('/company/stages', {
        params: { status: 'в работе', limit: 500, page: 1 },
      })
      const rows = response.data?.stages || []
      setAllocationCatalog(rows.map((row) => ({
        stage_id: row.id,
        stage_type: row.type,
        contract_id: row.contract_id,
        contract_number: row.contract_number,
        object_id: row.object_id,
        object_name: row.object_name,
      })))
    } catch (error) {
      alert('Не удалось загрузить стадии: ' + translateApiError(error.response?.data?.error || error.message))
      setAllocationCatalog([])
    } finally {
      setAllocationCatalogLoading(false)
    }
  }

  const submitAllocation = async () => {
    if (!canEditAllocation) return
    if (!employee?.id || !allocationStageId) return
    try {
      await api.post(`/company/employees/${employee.id}/allocations`, { stage_id: allocationStageId })
      setAllocationModalOpen(false)
      await fetchEmployeeAllocations(employee.id)
    } catch (error) {
      alert('Не удалось добавить проект: ' + translateApiError(error.response?.data?.error || error.message))
    }
  }

  if (loading) {
    return (
      <div className="company-page">
        <div className="loading">Загрузка...</div>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="company-page">
        <p>Сотрудник не найден</p>
        <Button variant="ghost" className="btn-back" onClick={back}>← Назад к списку</Button>
      </div>
    )
  }

  return (
    <div className="company-page employee-page employee-page-v2 employee-detail-page">
      <EmployeeDetailHeader
        employee={employee}
        actualTeamOptions={actualTeamOptions}
        teamOptions={teamOptions}
        selectedTeam={selectedTeam}
        showTeamSegment={showTeamSegment}
        getSystemRoleLabel={getSystemRoleLabel}
        onTeamChange={(value) => {
          setSelectedTeam(value)
          setTeamFilter('')
        }}
        actionItems={employeeActionItems}
        teamDataUrl={teamDataUrl}
      />

      <EmployeeMainTabs tabs={mainSectionTabs} activeTab={activeMainTab} onChange={setActiveMainTab} />

      <div
        id={SECTION_IDS.income}
        role="tabpanel"
        className="employee-detail-panel employee-section-income"
        aria-labelledby={`employee-main-tab-${SECTION_IDS.income}`}
        hidden={activeMainTab !== SECTION_IDS.income}
      >
        <div className="employee-detail-periodbar">
          <span className="employee-detail-period-label">Период</span>
          <SegmentedControl
            ariaLabel="Период доходов"
            options={PERIOD_OPTIONS}
            value={periodType === 'year' ? 'year' : `q${selectedQuarter}`}
            onChange={(value) => {
              if (value === 'year') {
                setPeriodType('year')
                return
              }
              setPeriodType('quarter')
              setSelectedQuarter(Number(String(value).replace('q', '')))
            }}
          />
          <span className="employee-detail-period-label employee-detail-period-label-year">Год</span>
          <SegmentedControl
            ariaLabel="Выбор года"
            options={YEAR_OPTIONS.map((year) => ({ value: year, label: String(year) }))}
            value={selectedYear}
            onChange={(value) => setSelectedYear(Number(value))}
          />
        </div>

        <section className={`employee-income-hero${incomeModel.heroAmount > 0 ? '' : ' employee-income-hero--empty'}`}>
          <h2 className="employee-detail-sr-only">Заработано</h2>
          <p className="employee-income-hero-overline">Заработано за {periodLabel}</p>
          <div className="employee-income-hero-amount">
            {formatCurrency(incomeModel.heroAmount).replace(/\s?₽$/, '')}
            <span>₽</span>
          </div>
          {incomeModel.heroAmount > 0 && heroNote && (
            <div className="employee-income-hero-note">
              <Icon name="check" size={18} aria-hidden />
              <span>{heroNote}</span>
            </div>
          )}
          {incomeModel.heroAmount === 0 && (
            <div className="employee-income-hero-note employee-income-hero-note-muted">
              <Icon name="help" size={18} aria-hidden />
              <span>В этом периоде нет подписанных актов. Посмотри, что у тебя в работе.</span>
            </div>
          )}
          {isOwnEmployeeCard && displayedPayoutRequest && displayedPayoutRequest.wish_amount !== null && displayedPayoutRequest.wish_amount !== undefined && (
            <div className="employee-income-hero-requested">
              <span className="employee-income-hero-requested__label">Вы запросили к получению</span>
              <span className="employee-income-hero-requested__amount">{formatCurrency(displayedPayoutRequest.wish_amount)}</span>
              {displayedPayoutRequest.status === 'collecting' && (
                <button
                  type="button"
                  className="employee-income-hero-requested__edit"
                  onClick={() => openPayoutModal(displayedPayoutRequest)}
                >
                  <Icon name="edit" size={14} aria-hidden /> Изменить
                </button>
              )}
            </div>
          )}
        </section>

        {periodType === 'year' && (
          <section className="employee-detail-section employee-income-year-section">
            <div className="employee-detail-section-head">
              <h2>По кварталам</h2>
              <span>{selectedYear} год · итого {formatCurrency(incomeModel.heroAmount)}</span>
            </div>
            <div className="employee-income-quarters" aria-label="График доходов по кварталам">
              {incomeModel.quarters.map((quarter) => {
                const maxAmount = Math.max(...incomeModel.quarters.map((item) => item.amount), 1)
                const height = quarter.amount > 0 ? Math.max(4, Math.round((quarter.amount / maxAmount) * 100)) : 0
                return (
                  <div key={quarter.quarter} className="employee-income-quarter">
                    <strong className={quarter.amount > 0 ? '' : 'employee-income-quarter-zero'}>
                      {formatCurrency(quarter.amount)}
                    </strong>
                    <div className={`employee-income-quarter__wrap${quarter.quarter === selectedQuarter ? ' employee-income-quarter__wrap-current' : ''}`}>
                      <div
                        className={`employee-income-quarter__bar${quarter.amount > 0 ? '' : ' employee-income-quarter__bar-zero'}`}
                        style={{ height: `${height}%` }}
                      />
                    </div>
                    <span>{QUARTER_LABELS[quarter.quarter] || quarter.quarter} квартал</span>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        <section className="employee-detail-section employee-income-breakdown-section">
          <div className="employee-detail-section-head">
            <h2>Из чего складывается</h2>
            <span>
              {incomeModel.breakdown.length} {pluralizeRu(incomeModel.breakdown.length, 'проект', 'проекта', 'проектов')}
              {' · '}
              {incomeModel.breakdown.reduce((sum, row) => sum + row.actsCount, 0)} {pluralizeRu(incomeModel.breakdown.reduce((sum, row) => sum + row.actsCount, 0), 'акт', 'акта', 'актов')}
              {' · '}
              {periodLabel}
            </span>
          </div>
          <TableShell className="employee-detail-table employee-detail-table-scroll">
            <table className="ds-data-table ds-income-table department-detail-data-table employee-income-table">
              <colgroup>
                <col className="employee-income-col-project" />
                <col className="employee-income-col-acts" />
                <col className="employee-income-col-amount" />
              </colgroup>
              <thead>
                <tr>
                  <th>Проект</th>
                  <th>Акты</th>
                  <th className="ds-align-right">Сумма (моя доля)</th>
                </tr>
              </thead>
              <tbody>
                {incomeModel.breakdown.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="employee-detail-empty-cell">Нет актов по выбранным фильтрам.</td>
                  </tr>
                ) : incomeModel.breakdown.map((row, index) => {
                  const stageRaw = stageInfoById.get(String(row.stageId || '').trim()) || {}
                  const objectName = String(stageRaw.объект || stageRaw.object_name || '').trim()
                  const subtitle = buildStageSubtitle({ contractNumber: row.project, stageRaw })
                  return (
                  <tr key={`${row.project}-${row.stage}-${row.team}`}>
                    <td>
                      <span className="employee-income-stage-cell">
                        <span className={`employee-income-dot employee-income-dot-palette-${(index % 7) + 1}`} aria-hidden />
                        <span>
                          <strong>{objectName || row.project || 'Без объекта'}</strong>
                          {row.stageId ? (
                            <Link
                              to={`/stage/${encodeURIComponent(row.stageId)}`}
                              className="employee-income-stage-link"
                              aria-label={String(row.stageId)}
                            >
                              {subtitle || row.stageId}
                            </Link>
                          ) : (
                            <small>{subtitle || 'Без стадии'}</small>
                          )}
                        </span>
                      </span>
                    </td>
                    <td>{row.actsCount} {pluralizeRu(row.actsCount, 'акт', 'акта', 'актов')}</td>
                    <td className="ds-mono">{formatCurrency(row.amount)}</td>
                  </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>Итого по проектам</td>
                  <td className="ds-mono">
                    {formatCurrency(breakdownTotal)}
                    <span className="employee-income-total-check" aria-hidden>✓</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </TableShell>
        </section>

        <section className="employee-detail-section employee-detail-section-soft employee-income-remaining-section">
          <div className="employee-detail-section-head">
            <h2>Что ещё можешь заработать</h2>
            <span>
              {remainingRows.length} {pluralizeRu(remainingRows.length, 'стадия', 'стадии', 'стадий')}
              {' · '}
              {remainingFilter === 'all' ? 'все статусы' : REMAINING_FILTERS.find((filter) => filter.value === remainingFilter)?.label.toLowerCase()}
            </span>
          </div>
          <div className="employee-remain-filter" role="tablist" aria-label="Фильтр оставшихся стадий">
            {REMAINING_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                role="tab"
                aria-selected={remainingFilter === filter.value}
                className={`employee-remain-chip${remainingFilter === filter.value ? ' employee-remain-chip-active' : ''}`}
                onClick={() => setRemainingFilter(filter.value)}
              >
                {filter.label}
                <span>{remainingCounts[filter.value] ?? 0}</span>
              </button>
            ))}
          </div>
          <TableShell className="employee-detail-table employee-detail-table-scroll">
            <table className="ds-data-table ds-income-table department-detail-data-table employee-income-table employee-remaining-income-table">
              <colgroup>
                <col className="employee-income-col-project" />
                <col className="employee-income-col-status" />
                <col className="employee-income-col-date" />
                <col className="employee-income-col-amount" />
                <col className="employee-income-col-work" />
              </colgroup>
              <thead>
                <tr>
                  <th>Проект</th>
                  <th>Статус</th>
                  <th>Срок сдачи</th>
                  <th className="ds-align-right">Моя доля</th>
                  <th className="ds-align-center">В работе</th>
                </tr>
              </thead>
              <tbody>
                {remainingRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="employee-detail-empty-cell">В этом срезе стадий нет.</td>
                  </tr>
                ) : remainingRows.map((row, index) => {
                  const stageAllocation = findAllocationByStageKey(row.stageId)
                  const isInProgress = (stageAllocation?.work_status || '') === 'in_progress'
                  const projectLabel = row.object || row.stageId || 'Без проекта'
                  const remainingSubtitle = buildStageSubtitle({ stageRaw: row.stage })
                  const visualStatus = getStageVisualStatus(row.status, {
                    dueDate: row.dueDate,
                    isInvalidLegacyDate,
                  })
                  return (
                    <tr key={`${row.stageId}-${row.team}`}>
                      <td>
                        <span className="employee-income-stage-cell">
                          <span className={`employee-income-dot employee-income-dot-palette-${(index % 7) + 1}`} aria-hidden />
                          <span>
                            <strong>{projectLabel}</strong>
                            {row.stageId ? (
                              <Link to={`/stage/${encodeURIComponent(row.stageId)}`}>{remainingSubtitle || row.stageId}</Link>
                            ) : (
                              <small>{remainingSubtitle || 'Без стадии'}</small>
                            )}
                          </span>
                        </span>
                      </td>
                      <td>
                        <span className={getStageStatusPillClass(visualStatus)}>
                          {visualStatus.label}
                        </span>
                      </td>
                      <td
                        className={`employee-income-due-cell${visualStatus.isOverdue ? ' employee-income-due-cell-overdue' : ''}`}
                        {...(visualStatus.isOverdue
                          ? { 'aria-label': `Срок просрочен: ${formatDate(row.dueDate) || '—'}` }
                          : {})}
                      >
                        {formatDate(row.dueDate) || '—'}
                      </td>
                      <td className="ds-mono">{formatCurrency(row.remaining)}</td>
                      <td className="ds-align-center">
                        <input
                          type="checkbox"
                          aria-label={`В работе ${row.stageId}`}
                          checked={isInProgress}
                          disabled={!canEditAllocation || allocationUpdatingStageId === row.stageId}
                          onChange={async () => {
                            if (!canEditAllocation) return
                            if (!stageAllocation) {
                              await api.post(`/company/employees/${employee.id}/allocations`, { stage_id: row.stageId })
                              await fetchEmployeeAllocations(employee.id)
                            }
                            updateAllocationStatus(row.stageId, isInProgress ? 'new' : 'in_progress')
                          }}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Всего</td>
                  <td className="ds-mono">{formatCurrency(remainingRowsTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </TableShell>
          <p className="employee-income-footer-link">
            <button type="button" className="ui-btn ui-btn-link" onClick={() => setActiveMainTab(SECTION_IDS.projects)}>
              Посмотреть все мои стадии и потенциальный доход
            </button>
          </p>
        </section>
      </div>

      <div
        id={SECTION_IDS.projects}
        role="tabpanel"
        className="employee-projects-panel department-stages-panel"
        aria-labelledby={`employee-main-tab-${SECTION_IDS.projects}`}
        hidden={activeMainTab !== SECTION_IDS.projects}
      >
        <div className="employee-pr-section-heading">
          <div className="employee-pr-section-title-row">
            <h2 className="employee-pr-section-title">Стадии ({filteredStages.length})</h2>
          </div>
        </div>
          <div className="department-stages-filterbar employee-projects-filters">
              {uniqueStageTypes.length > 0 && (
                <FormGroup label="Тип стадии" htmlFor="employee-projects-filter-stage-type" className="department-stage-filter">
                  <Select
                    id="employee-projects-filter-stage-type"
                    value={stageTypeFilter}
                    onChange={(e) => setStageTypeFilter(e.target.value)}
                    aria-label="Тип стадии"
                  >
                    <option value="">Все</option>
                    {uniqueStageTypes.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </Select>
                </FormGroup>
              )}
              {uniqueDueDatePeriods.length > 0 && (
                <FormGroup label="Срок (год)" htmlFor="employee-projects-filter-due" className="department-stage-filter">
                  <Select
                    id="employee-projects-filter-due"
                    value={dueDateFilter}
                    onChange={(e) => setDueDateFilter(e.target.value)}
                    aria-label="Год срока исполнения"
                  >
                    <option value="">Все</option>
                    {uniqueDueDatePeriods.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </Select>
                </FormGroup>
              )}
              {uniqueStatuses.length > 0 && (
                <FormGroup label="Статус" htmlFor="employee-projects-filter-status" className="department-stage-filter">
                  <Select
                    id="employee-projects-filter-status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Статус стадии"
                  >
                    <option value="">Все</option>
                    {uniqueStatuses.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </Select>
                </FormGroup>
              )}
              {uniqueTeams.length > 1 && !showTeamSegment && (
                <FormGroup label="Команда" htmlFor="employee-projects-filter-team" className="department-stage-filter">
                  <Select
                    id="employee-projects-filter-team"
                    value={teamFilter}
                    onChange={(e) => setTeamFilter(e.target.value)}
                    aria-label="Команда"
                  >
                    <option value="">Все</option>
                    {uniqueTeams.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </Select>
                </FormGroup>
              )}
              <FormGroup label="Поиск" htmlFor="employee-projects-search" className="department-stage-filter department-stage-filter-grow">
                <Input
                  id="employee-projects-search"
                  type="search"
                  placeholder="Объект, договор или № стадии"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Поиск по объекту, договору или номеру стадии"
                />
              </FormGroup>
          </div>

          {filteredStages.length === 0 ? (
            <p className="no-projects-message">Нет проектов по выбранным фильтрам.</p>
          ) : (
            <>
              <TableShell className="department-detail-table department-stages-table-shell employee-stages-table-shell">
                <table className="ds-data-table department-detail-data-table department-stages-table employee-stage-overview-table my-projects-table">
                  <colgroup>
                    <col className="department-stage-col-name" />
                    <col className="department-stage-col-income" />
                    <col className="department-stage-col-progress" />
                    <col className="department-stage-col-earned" />
                    <col className="department-stage-col-remaining" />
                    <col className="department-stage-col-due" />
                    <col className="department-stage-col-status" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>№ стадии</th>
                      <th className="ds-align-right">Моя доля (бюджет)</th>
                      <th className="ds-align-right">% работы</th>
                      <th className="ds-align-right">Заактировано</th>
                      <th className="ds-align-right">Ещё получишь</th>
                      <th>Срок</th>
                      <th>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeObjectGroups.map((objectGroup) => {
                      const isCollapsed = collapsedStageObjects.has(objectGroup.object)
                      return (
                        <React.Fragment key={`object-group-${objectGroup.object}`}>
                          <tr className="department-stage-group-row">
                            <td colSpan={7}>
                              <div className="department-stage-group-cell">
                                <button
                                  type="button"
                                  className="department-stage-group-toggle"
                                  aria-expanded={!isCollapsed}
                                  aria-label={`${isCollapsed ? 'Развернуть' : 'Свернуть'} объект ${objectGroup.object}`}
                                  onClick={() => toggleStageObjectGroup(objectGroup.object)}
                                >
                                  <Icon name={isCollapsed ? 'chevronRight' : 'chevronDown'} size={14} />
                                </button>
                                {objectGroup.firstStage?.object_id ? (
                                  <Link to={`/object/${objectGroup.firstStage.object_id}`} className="department-stage-object-link">
                                    {objectGroup.object}
                                  </Link>
                                ) : (
                                  <strong>{objectGroup.object}</strong>
                                )}
                                <span className="department-stage-group-count">{objectGroup.itemCount}</span>
                              </div>
                            </td>
                          </tr>
                          {!isCollapsed && objectGroup.typeGroups.map((typeGroup) => {
                            const typeGroupKey = `${objectGroup.objectKey}::${typeGroup.typeKey}`
                            const isTypeExpanded = expandedTypeGroups.has(typeGroupKey)
                            const metrics = typeGroup.items.map((stage) => {
                              const earnedAmount = getEarnedAmount(stage)
                              const actuatedCombined = getActuatedCombinedForRow(stage)
                              const actuatedPrevious = selectedYear === 2024 ? 0 : getActuatedPreviousYears(stage)
                              const remaining = earnedAmount - getActuatedAmount2025(stage) - actuatedPrevious
                              const pct = workPercentages[stage.id_стадии] ?? 100
                              const hasBudget = stage.доход_сотрудника_по_стоимости != null
                              const budget = hasBudget ? parseFloat(stage.доход_сотрудника_по_стоимости || 0) : 0
                              const visualStatus = getStageVisualStatus(getStageStatus(stage), {
                                dueDate: stage.срок_исполнения,
                                isInvalidLegacyDate,
                              })
                              return { stage, team: stage._team, earnedAmount, actuatedCombined, remaining, pct, hasBudget, budget, visualStatus }
                            })
                            const budgetTotal = metrics.reduce((sum, item) => sum + item.budget, 0)
                            const actedTotal = metrics.reduce((sum, item) => sum + item.actuatedCombined, 0)
                            const remainingTotal = metrics.reduce((sum, item) => sum + item.remaining, 0)
                            const workPct = budgetTotal > 0
                              ? Math.round(metrics.reduce((sum, item) => sum + item.pct * item.budget, 0) / budgetTotal)
                              : 100
                            const representative = metrics.reduce((best, item) => (
                              (item.visualStatus.isOverdue && !best.visualStatus.isOverdue) ? item : best
                            ), metrics[0])
                            const budgetSub = typeGroup.items.length > 1
                              ? `${typeGroup.items.length} ${pluralizeStageBudgets(typeGroup.items.length)}`
                              : (metrics[0]?.stage.личный_процент_сотрудника != null
                                ? `${parseFloat(metrics[0].stage.личный_процент_сотрудника || 0).toFixed(1)}% команды`
                                : '')
                            const groupBudgetPath = getSharedBudgetCardPath(metrics, (item) => item.stage)
                            const singleStage = typeGroup.items.length === 1 ? typeGroup.items[0] : null
                            const singleStageId = singleStage?.id_стадии || singleStage?.stage_id || ''
                            const typeGroupLabel = singleStage ? formatStageLabel(singleStage) : typeGroup.typeKey

                            return (
                              <React.Fragment key={typeGroupKey}>
                                <tr
                                  className={`department-stage-type-row ${isTypeExpanded ? 'is-open' : ''}`}
                                  onClick={(event) => {
                                    if (event.target.closest('a, button, input')) return
                                    toggleTypeGroup(typeGroupKey)
                                  }}
                                >
                                  <td>
                                    <div className="department-stage-type-inner">
                                      <span className="department-stage-type-chevron" aria-hidden>
                                  <Icon name="chevronRight" size={12} />
                                </span>
                                      {singleStageId && !groupBudgetPath ? (
                                        <Link
                                          to={`/stage/${encodeURIComponent(singleStageId)}`}
                                          className="department-stage-type-name"
                                          onClick={(event) => event.stopPropagation()}
                                        >
                                          {typeGroupLabel}
                                        </Link>
                                      ) : (
                                        <button
                                          type="button"
                                          className="department-stage-type-name"
                                          onClick={() => toggleTypeGroup(typeGroupKey)}
                                        >
                                          {typeGroupLabel}
                                        </button>
                                      )}
                                      <span className="department-stage-type-count">{typeGroup.items.length}</span>
                                      {groupBudgetPath ? (
                                        <Link
                                          to={groupBudgetPath}
                                          className="department-stage-type-detail"
                                          aria-label={`Открыть этап ${typeGroup.typeKey}`}
                                          onClick={(event) => event.stopPropagation()}
                                        >
                                          <Icon name="chevronRight" size={14} />
                                        </Link>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td className="ds-mono ds-align-right department-stage-amount">
                                    {budgetTotal > 0 ? (
                                      <>
                                        {formatCurrency(budgetTotal)}
                                        {budgetSub ? <small className="department-stage-budget-sub">{budgetSub}</small> : null}
                                      </>
                                    ) : '—'}
                                  </td>
                                  <td className="ds-mono ds-align-right">
                                    {budgetTotal > 0 ? (
                                      <span className="department-stage-progress employee-stage-progress">
                                        <span className="department-stage-progress-track" aria-hidden>
                                          <span style={{ width: `${Math.min(100, Math.max(0, workPct))}%` }} />
                                        </span>
                                        <span>{workPct}%</span>
                                      </span>
                                    ) : '—'}
                                  </td>
                                  <td className={`ds-mono ds-align-right department-stage-amount ${actedTotal > 0 ? 'department-stage-amount-earned employee-pr-earned' : ''}`}>
                                    {formatCurrency(actedTotal)}
                                  </td>
                                  <td className={`ds-mono ds-align-right department-stage-amount ${remainingTotal < 0 ? 'employee-pr-overrun' : ''}`}>
                                    {budgetTotal > 0 || actedTotal > 0 ? formatCurrency(remainingTotal) : '—'}
                                  </td>
                                  <td className={representative?.visualStatus.isOverdue ? 'employee-income-due-cell employee-income-due-cell-overdue' : 'employee-income-due-cell'}>
                                    {formatDate(representative?.stage.срок_исполнения) || '—'}
                                  </td>
                                  <td>
                                    <span className={getStageStatusPillClass(representative?.visualStatus)}>
                                      {representative?.visualStatus.label}
                                    </span>
                                  </td>
                                </tr>
                                {isTypeExpanded && metrics.map(({ stage, team, actuatedCombined, remaining, pct, hasBudget, visualStatus }) => {
                                  const dateStr = formatDate(stage.срок_исполнения)
                                  const invalidDate = isInvalidLegacyDate(stage.срок_исполнения)
                                  return (
                                    <tr key={`${objectGroup.object}-${team}-${stage.id_стадии}`} className="employee-stage-data-row department-stage-detail-row">
                                      <td>
                                        {getBudgetCardPath(stage) ? (
                                          <Link
                                            to={getBudgetCardPath(stage)}
                                            className="department-stage-link"
                                          >
                                            {formatStageLabel(stage)}
                                          </Link>
                                        ) : formatStageLabel(stage)}
                                        {showTeamColumn && (
                                          <small className="employee-stage-team-note">{team}</small>
                                        )}
                                      </td>
                                      <td className="ds-mono ds-align-right department-stage-amount">
                                        {hasBudget ? (
                                          <>
                                            {formatCurrency(stage.доход_сотрудника_по_стоимости || 0)}
                                            {stage.личный_процент_сотрудника != null && (
                                              <small className="department-stage-budget-sub">
                                                {parseFloat(stage.личный_процент_сотрудника || 0).toFixed(1)}% команды
                                              </small>
                                            )}
                                          </>
                                        ) : '—'}
                                      </td>
                                      <td className="ds-mono ds-align-right">
                                        {!hasBudget ? '—' : (
                                          <span className="department-stage-progress employee-stage-progress">
                                            <span className="department-stage-progress-track" aria-hidden>
                                              <span style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
                                            </span>
                                            <span>{pct}%</span>
                                            <Input
                                              type="number"
                                              min={0}
                                              max={100}
                                              value={pct}
                                              onChange={(e) => handleWorkPercentageChange(stage.id_стадии, e.target.value)}
                                              className="work-percentage-input employee-stage-progress-input"
                                              aria-label={`Процент работы для ${formatStageLabel(stage)}`}
                                            />
                                          </span>
                                        )}
                                      </td>
                                      <td className={`ds-mono ds-align-right department-stage-amount ${actuatedCombined > 0 ? 'department-stage-amount-earned employee-pr-earned' : ''}`}>
                                        {formatCurrency(actuatedCombined)}
                                      </td>
                                      <td className={`ds-mono ds-align-right department-stage-amount ${remaining < 0 ? 'employee-pr-overrun' : ''} ${!hasBudget && remaining > 0 ? 'employee-pr-summary-remain-warm' : ''}`}>
                                        {hasBudget || actuatedCombined > 0 ? formatCurrency(remaining) : '—'}
                                      </td>
                                      <td
                                        className={
                                          invalidDate
                                            ? 'employee-pr-date-invalid'
                                            : visualStatus.isOverdue
                                              ? 'employee-income-due-cell employee-income-due-cell-overdue'
                                              : 'employee-income-due-cell'
                                        }
                                        {...(visualStatus.isOverdue && !invalidDate
                                          ? { 'aria-label': `Срок просрочен: ${dateStr || '—'}` }
                                          : {})}
                                      >
                                        {invalidDate && dateStr ? dateStr : dateStr || '—'}
                                      </td>
                                      <td>
                                        <span className={getStageStatusPillClass(visualStatus)}>
                                          {visualStatus.label}
                                        </span>
                                      </td>
                                    </tr>
                                  )
                                })}
                              </React.Fragment>
                            )
                          })}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="employee-pr-summary-row">
                      <td>ИТОГО</td>
                      <td className="ds-mono ds-align-right department-stage-amount">
                        {formatCurrency(projectsTableTotals.budget)}
                      </td>
                      <td />
                      <td className={`ds-mono ds-align-right department-stage-amount ${projectsActuatedCombinedTotal > 0 ? 'department-stage-amount-earned' : ''}`}>
                        {formatCurrency(projectsActuatedCombinedTotal)}
                      </td>
                      <td
                        className={`ds-mono ds-align-right department-stage-amount ${
                          projectsRemainTotal < 0 ? 'employee-pr-overrun' : 'employee-pr-summary-remain-warm'
                        }`}
                      >
                        {formatCurrency(projectsRemainTotal)}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </TableShell>

              <div className="department-stages-mobile-list employee-stages-mobile-list" aria-label="Стадии сотрудника списком">
                {Object.keys(groupedStages).sort().map((object) => {
                  const teams = groupedStages[object]
                  const objectStageCount = Object.keys(teams).reduce(
                    (n, tk) => n + (teams[tk] || []).length,
                    0
                  )
                  const firstTeam = Object.keys(teams).sort()[0]
                  const firstStage = (teams[firstTeam] || [])[0]
                  const isCollapsed = collapsedStageObjects.has(object)

                  return (
                    <section key={`mobile-object-group-${object}`} className="department-stage-mobile-group" aria-label={object}>
                      <div className="department-stage-mobile-group-head">
                        <button
                          type="button"
                          className="department-stage-group-toggle"
                          aria-expanded={!isCollapsed}
                          aria-label={`${isCollapsed ? 'Развернуть' : 'Свернуть'} объект ${object}`}
                          onClick={() => toggleStageObjectGroup(object)}
                        >
                          <Icon name={isCollapsed ? 'chevronRight' : 'chevronDown'} size={14} />
                        </button>
                        {firstStage?.object_id ? (
                          <Link to={`/object/${firstStage.object_id}`} className="department-stage-object-link">
                            {object}
                          </Link>
                        ) : (
                          <strong>{object}</strong>
                        )}
                        <span>{objectStageCount} {pluralizeRu(objectStageCount, 'стадия', 'стадии', 'стадий')}</span>
                      </div>
                      {!isCollapsed && Object.keys(teams).sort().map((team) => (
                        (teams[team] || []).map((stage) => {
                          const earnedAmount = getEarnedAmount(stage)
                          const actuatedCombined = getActuatedCombinedForRow(stage)
                          const actuatedPrevious = selectedYear === 2024 ? 0 : getActuatedPreviousYears(stage)
                          const remaining = earnedAmount - getActuatedAmount2025(stage) - actuatedPrevious
                          const status = getStageStatus(stage)
                          const pct = workPercentages[stage.id_стадии] ?? 100
                          const hasBudget = stage.доход_сотрудника_по_стоимости != null
                          const dateStr = formatDate(stage.срок_исполнения)
                          const visualStatus = getStageVisualStatus(status, {
                            dueDate: stage.срок_исполнения,
                            isInvalidLegacyDate,
                          })

                          return (
                            <article key={`${object}-${team}-${stage.id_стадии}-mobile`} className="department-stage-mobile-card">
                              <div className="department-stage-mobile-title">
                                {getBudgetCardPath(stage) ? (
                                  <Link
                                    to={getBudgetCardPath(stage)}
                                    className="department-stage-link"
                                  >
                                    {formatStageLabel(stage)}
                                  </Link>
                                ) : (
                                  <strong>{formatStageLabel(stage)}</strong>
                                )}
                                <span className={getStageStatusPillClass(visualStatus)}>{visualStatus.label}</span>
                              </div>
                              {showTeamColumn && <small className="employee-stage-team-note">{team}</small>}
                              {hasBudget && (
                                <div className="department-stage-mobile-progress employee-stage-mobile-progress">
                                  <span className="department-stage-progress-track">
                                    <span style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
                                  </span>
                                  <span>{pct}%</span>
                                  <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={pct}
                                    onChange={(e) => handleWorkPercentageChange(stage.id_стадии, e.target.value)}
                                    className="work-percentage-input employee-stage-progress-input employee-stage-mobile-progress-input"
                                    aria-label={`Процент работы для ${formatStageLabel(stage)}`}
                                  />
                                </div>
                              )}
                              <dl className="department-stage-mobile-metrics">
                                <div>
                                  <dt>Моя доля</dt>
                                  <dd>{hasBudget ? formatCurrency(stage.доход_сотрудника_по_стоимости || 0) : '—'}</dd>
                                </div>
                                <div>
                                  <dt>Заактировано</dt>
                                  <dd>{formatCurrency(actuatedCombined)}</dd>
                                </div>
                                <div>
                                  <dt>Ещё получишь</dt>
                                  <dd>{hasBudget || actuatedCombined > 0 ? formatCurrency(remaining) : '—'}</dd>
                                </div>
                                <div>
                                  <dt>Срок</dt>
                                  <dd>{dateStr || '—'}</dd>
                                </div>
                              </dl>
                              {stage.личный_процент_сотрудника != null && (
                                <p className="department-stage-mobile-budget">
                                  {parseFloat(stage.личный_процент_сотрудника || 0).toFixed(1)}% команды
                                </p>
                              )}
                            </article>
                          )
                        })
                      ))}
                    </section>
                  )
                })}
              </div>
            </>
          )}
      </div>

      {activeMainTab === SECTION_IDS.allocation && (
        <EmployeeAllocationBoard
          columns={allocationColumns}
          stages={enrichedAllocationStages}
          loading={allocationLoading}
          canEdit={canEditAllocation}
          updatingStageId={allocationUpdatingStageId}
          menuStageId={allocationMenuStageId}
          draggedStageId={draggedAllocationStageId}
          statusActions={allocationStatusActions}
          preventCardClickRef={preventCardClickRef}
          onOpenAdd={openAllocationModal}
          onMenuChange={setAllocationMenuStageId}
          onStatusChange={updateAllocationStatus}
          onDragStart={(event, stageId) => {
            event.dataTransfer.effectAllowed = 'move'
            setDraggedAllocationStageId(stageId)
          }}
          onDragEnd={() => {
            preventCardClickRef.current = true
            setDraggedAllocationStageId(null)
          }}
          onDrop={handleAllocationDrop}
          formatDate={formatAllocationDate}
        />
      )}

      {allocationModalOpen && (
        <ConfirmModal
          title="Добавить проект в аллокацию"
          message={(
            <div className="create-act-modal-content">
              {allocationCatalogLoading ? (
                <p>Загрузка стадий...</p>
              ) : (
                <>
                  <div className="filter-group">
                    <SearchableSelect
                      label="Объект"
                      value={allocationObjectId}
                      placeholder="Начните вводить объект"
                      options={allocationObjectOptions.map((item) => ({
                        value: item.id,
                        label: item.name,
                      }))}
                      onChange={(value) => {
                        setAllocationObjectSearch('')
                        setAllocationObjectId(value)
                        setAllocationContractId('')
                        setAllocationStageId('')
                      }}
                    />
                  </div>

                  <div className="filter-group">
                    <SearchableSelect
                      label="Договор"
                      value={allocationContractId}
                      placeholder="Номер договора"
                      options={allocationContractOptions.map((item) => ({
                        value: item.id,
                        label: item.number,
                      }))}
                      onChange={(value) => {
                        const nextContractId = value
                        setAllocationContractSearch('')
                        setAllocationContractId(nextContractId)
                        setAllocationStageId('')
                        const sample = allocationAvailableStages.find((row) => String(row.contract_id || '') === nextContractId)
                        if (sample?.object_id) setAllocationObjectId(String(sample.object_id))
                      }}
                    />
                  </div>

                  <div className="filter-group">
                    <SearchableSelect
                      label="Стадия"
                      value={allocationStageId}
                      placeholder="ID или тип стадии"
                      options={allocationStageOptions.map((item) => ({
                        value: item.stage_id,
                        label: formatStageLabel(item),
                        description: [item.object_name, item.contract_number].filter(Boolean).join(' · '),
                        searchText: [item.stage_id, item.stage_type, item.object_name, item.contract_number].filter(Boolean).join(' '),
                      }))}
                      onChange={(value) => {
                        const nextStageId = value
                        setAllocationStageSearch('')
                        setAllocationStageId(nextStageId)
                        const sample = allocationAvailableStages.find((row) => String(row.stage_id || '') === nextStageId)
                        if (sample?.object_id) setAllocationObjectId(String(sample.object_id))
                        if (sample?.contract_id) setAllocationContractId(String(sample.contract_id))
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          confirmLabel="+ Добавить проект"
          cancelLabel="Отмена"
          confirmVariant="primary"
          onConfirm={submitAllocation}
          onCancel={() => setAllocationModalOpen(false)}
          confirmDisabled={allocationCatalogLoading || !allocationStageId}
        />
      )}

      {employee?.id && (
        <EntityHistory entityType="employees" entityId={employee.id} />
      )}

      {employeeToDelete && (
        <ConfirmModal
          title="Архивировать сотрудника?"
          message={(
            <p>
              Сотрудник <strong>{employeeToDelete.full_name}</strong> будет архивирован.
              Исторические распределения доходов сохранятся.
            </p>
          )}
          confirmLabel="Архивировать"
          cancelLabel="Отмена"
          confirmVariant="danger"
          onConfirm={handleArchiveEmployee}
          onCancel={() => { setEmployeeToDelete(null); setEmployeeDeleteError(null); }}
          error={employeeDeleteError}
        />
      )}
    </div>
  )
}

export default EmployeeCard
